import React, { ReactElement, useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';

import { createUrl, request } from 'app/utils';
import { Box, Flex, Loading, Page, Section, Typography } from 'components';
import { BadgesApiResponse, BadgesList } from 'features/badges';
import { useSchool } from 'features/school';
import { useBadges, StudentBadgesApiResponse } from 'features/badges';

import styled from 'styled-components';
import { useMenu } from 'features/menu';

const SquareLines = styled('i')`
  position: relative;
  position: absolute;
  left: 0;
  width: 0px;
  height: 0px;

  &::after {
    content: '';
    display: block;
    background-color: ${({ theme }) => theme.colors.primary_200};
    position: absolute;
    z-index: 10;
  }

  &::before {
    content: '';
    display: block;

    border-top: 2px solid ${({ theme }) => theme.colors.dark_150};
    border-right: 2px solid ${({ theme }) => theme.colors.dark_150};
    position: absolute;

    right: 0px;
    z-index: 1;
  }

  ${({ theme: { mediaQueries } }) => {
    return `
    ${mediaQueries.mobile} {
      top: 1px;
      left: -25px;

      &::after {
        width: 14px;
        height: 14px;
      }

      &::before {
        top: -30px;
        width: 150px;
        height: 230px;
      }
    }

    ${mediaQueries.tablet} {
      left: -30px;

      &::after {
        width: 14px;
        height: 14px;
        top: 3px;
        left: 0px;
      }

      &::before {
        top: -40px;
        width: 100px;
        height: 150px;
      }
    }

    ${mediaQueries.desktop} {
      top: 4px;
      left: -40px;

      &::after {
        width: 17px;
        height: 17px;
        top: 0px;
        left: 0px;
      }

      &::before {
        top: -40px;
        width: 150px;
        height: 280px;
      }
    }
  `;
  }}
`;

const MyBadgesListView = (): ReactElement => {
  const { apiGatewayUrl } = useSchool();
  const [loading, setLoading] = useState(true);

  const { badges, setBadges, clearBadges, studentBadges, setStudentBadges, clearStudentBadges } = useBadges();

  const { setBackPathname, clearBackPathname } = useMenu();

  const fetch = async () => {
    await Promise.all([
      request({
        url: `/api/v1/School/StudentBadges`,
        method: 'post',
        baseURL: apiGatewayUrl,
        data: {
          productSlug: null,
          specialOnly: null,
          itensPerQuery: null,
        },
      }).then((response: AxiosResponse<StudentBadgesApiResponse>) => {
        setStudentBadges(response.data);
      }),
      request({
        url: `/api/v1/School/Badges`,
        method: 'post',
        baseURL: apiGatewayUrl,
        data: {
          productSlug: null,
          specialOnly: null,
          itensPerQuery: null,
        },
      }).then((response: AxiosResponse<BadgesApiResponse>) => {
        setBadges(response.data);
      }),
    ]).then(() => {
      setLoading(false);
    });
  };

  useEffect(() => {
    fetch();

    setBackPathname(createUrl('dashboard'));

    return () => {
      clearStudentBadges();
      clearBadges();
      clearBackPathname();
    };
  }, []);

  if (loading) return <Loading label="Por favor, aguarde." />;

  return (
    <Page title="Minhas conquistas">
      <Flex flexGrow={0} bg="dark_350">
        <Section flexDirection={{ m: 'column', d: 'row' }} justifyContent="space-between">
          <Flex flexGrow={0} flexDirection="column" pt={{ m: 40, d: 40 }} pb={{ m: 60, d: 100 }}>
            <Box position="relative">
              <SquareLines />
              <Typography type="type5" color="white" fontWeight="bold" upper>
                Todas as
              </Typography>
            </Box>

            <Typography type="type11" color="white" fontWeight="bold" mt={2}>
              Conquistas
            </Typography>
          </Flex>
        </Section>
      </Flex>

      <Flex py={{ m: 30, t: 60, d: 90 }} bg="gray_200" flexGrow={0}>
        <Section flexDirection="column">
          <Flex justifyContent="space-between" flexWrap={{ m: 'wrap', d: 'nowrap' }} alignItems="flex-end">
            <Flex flexDirection="column">
              {/* <Typography type="type3" upper>
                Minhas conquistas
              </Typography> */}

              <Typography type="type8" fontWeight="bold">
                Minhas conquistas
              </Typography>
            </Flex>
          </Flex>

          {studentBadges && studentBadges.length ? (
            <BadgesList badges={studentBadges} />
          ) : (
            <Typography type="type2" fontWeight="bold" mt={2} color="alert_400">
              Você não possui nenhuma conquista.
            </Typography>
          )}
        </Section>
      </Flex>

      <Flex bg="gray_200" flexGrow={0}>
        <Section flexDirection="column">
          <Flex justifyContent="space-between" flexWrap={{ m: 'wrap', d: 'nowrap' }} alignItems="flex-end">
            <Flex flexDirection="column">
              {/* <Typography type="type3" upper>
                Minhas conquistas
              </Typography> */}

              <Typography type="type8" fontWeight="bold">
                Todas as conquistas
              </Typography>
            </Flex>
          </Flex>

          {badges && badges.length ? (
            <BadgesList badges={badges} />
          ) : (
            <Typography type="type2" fontWeight="bold" mt={2} color="alert_400">
              Nenhuma conquista encontrada.
            </Typography>
          )}
        </Section>
      </Flex>
    </Page>
  );
};

export default MyBadgesListView;
